import React, { useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import LabelAndDescription from '@lyearn/form/src/Form/components/InputFields/LabelAndDescription';
import { getDefaultOverrides } from '@lyearn/form/src/Form/consts';
import { useTranslation } from '@lyearn/i18n';
import { Box, SelectSize } from '@lyearn/ui';
import { FieldOverrides } from '@/components/Form';
import { CronFrequencyEnum, CustomFrequencyEnum, ScheduleInfoInput } from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import Warnings from '../Warning';
import Custom, { getCustomFrequencyInitialValue } from './components/Custom';
import Daily from './components/Daily';
import Monthly from './components/Monthly';
import StartsOnDateField from './components/StartsOnDateField';
import Weekly from './components/Weekly';
import Yearly from './components/Yearly';
import { DEFAULT_DAY_OF_WEEK, DEFAULT_WEEK_OF_MONTH } from './conts';
import { FrequencyComponentOverrides } from './types';
import { getDayOfMonthAndMonthOfYear, ScheduleInfoErrorConfig } from './utils';

interface Props {
  value?: ScheduleInfoInput;
  onChange: (nextValue: ScheduleInfoInput) => void;
  moduleName?: string;
  hideTimeSelection?: boolean;
  warnings?: string[];
  disabled?: boolean;
  allowedFrequencies?: CronFrequencyEnum[];
  allowedCustomFrequencies?: CustomFrequencyEnum[];
  errorConfig?: ScheduleInfoErrorConfig;
  size?: SelectSize;
  headerOverrides?: FieldOverrides;
  overrides?: FrequencyComponentOverrides;
}

const FrequencyComponents = {
  [CronFrequencyEnum.Daily]: {
    Component: Daily,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const { frequency, timezone, time, startDate, endScheduleInfo } = value;

      return { frequency, timezone, time, startDate, endScheduleInfo };
    },
  },
  [CronFrequencyEnum.Weekly]: {
    Component: Weekly,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const { frequency, timezone, time, dayOfWeek, startDate, endScheduleInfo, daysOfWeek } =
        value;

      return {
        frequency,
        timezone,
        time,
        daysOfWeek: daysOfWeek ?? (dayOfWeek ? [dayOfWeek] : [DEFAULT_DAY_OF_WEEK]),
        startDate,
        endScheduleInfo,
      };
    },
  },
  [CronFrequencyEnum.Fortnightly]: {
    Component: Weekly,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const { frequency, timezone, time, startDate, endScheduleInfo, dayOfWeek, daysOfWeek } =
        value;

      return {
        frequency,
        timezone,
        time,
        daysOfWeek: daysOfWeek ?? (dayOfWeek ? [dayOfWeek] : [DEFAULT_DAY_OF_WEEK]),
        startDate,
        endScheduleInfo,
      };
    },
  },
  [CronFrequencyEnum.Monthly]: {
    Component: Monthly,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const {
        frequency,
        timezone,
        time,
        startDate,
        endScheduleInfo,
        dayOfWeek,
        daysOfWeek,
        weekOfMonth,
        weeksOfMonth,
      } = value;

      return {
        frequency,
        timezone,
        time,
        startDate,
        endScheduleInfo,
        daysOfWeek: daysOfWeek ?? (dayOfWeek ? [dayOfWeek] : [DEFAULT_DAY_OF_WEEK]),
        weeksOfMonth: weeksOfMonth ?? (weekOfMonth ? [weekOfMonth] : [DEFAULT_WEEK_OF_MONTH]),
      };
    },
  },
  [CronFrequencyEnum.Quarterly]: {
    Component: Monthly,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const {
        frequency,
        timezone,
        time,
        startDate,
        endScheduleInfo,
        dayOfWeek,
        daysOfWeek,
        weekOfMonth,
        weeksOfMonth,
      } = value;

      return {
        frequency,
        timezone,
        time,
        startDate,
        endScheduleInfo,
        daysOfWeek: daysOfWeek ?? (dayOfWeek ? [dayOfWeek] : [DEFAULT_DAY_OF_WEEK]),
        weeksOfMonth: weeksOfMonth ?? (weekOfMonth ? [weekOfMonth] : [DEFAULT_WEEK_OF_MONTH]),
      };
    },
  },
  [CronFrequencyEnum.Yearly]: {
    Component: Yearly,
    getInitialValue: (value: ScheduleInfoInput): ScheduleInfoInput => {
      const { frequency, timezone, time, dayOfMonth, MonthOfYear, startDate, endScheduleInfo } =
        value;
      const update_field = getDayOfMonthAndMonthOfYear(value.startDate);

      return {
        frequency,
        timezone,
        time,
        startDate,
        endScheduleInfo,
        dayOfMonth: dayOfMonth ?? update_field.dayOfMonth,
        MonthOfYear: MonthOfYear ?? update_field.MonthOfYear,
      };
    },
  },
  [CronFrequencyEnum.Custom]: {
    Component: Custom,
    getInitialValue: getCustomFrequencyInitialValue,
  },
  [CronFrequencyEnum.Hourly]: {
    Component: () => <></>,
    getInitialValue: (value: ScheduleInfoInput) => ({
      ...value,
    }),
  },
};

export const DEFAULT_SCHEDULE_INFO_INPUT: ScheduleInfoInput = {
  frequency: CronFrequencyEnum.Fortnightly,
  daysOfWeek: [DEFAULT_DAY_OF_WEEK],
  timezone: getCurrentTimeZoneValue(),
  time: '09:00',
  startDate: new Date().toISOString(),
};

function FrequencyFormField(props: Props) {
  const {
    value = DEFAULT_SCHEDULE_INFO_INPUT,
    onChange: onChangeProp,
    moduleName,
    hideTimeSelection,
    warnings,
    disabled,
    allowedFrequencies,
    allowedCustomFrequencies,
    errorConfig,
    size,
    headerOverrides,
    overrides,
  } = props;

  const { t } = useTranslation(['1on1s']);

  const onChange = useCallback(
    (updatedValues: Partial<ScheduleInfoInput>) => {
      if (updatedValues.frequency && value.frequency !== updatedValues.frequency) {
        const { getInitialValue } = FrequencyComponents[updatedValues.frequency];
        onChangeProp(getInitialValue({ ...value, ...updatedValues }));
      } else {
        onChangeProp({ ...value, ...updatedValues });
      }
    },
    [onChangeProp, value],
  );

  const onChangeDateField = React.useCallback(
    (selectedTime: string) => {
      const update_field = getDayOfMonthAndMonthOfYear(selectedTime);

      if (value.frequency === CronFrequencyEnum.Custom) {
        onChangeProp({
          ...value,
          startDate: selectedTime,
          custom: {
            ...(value.custom as any),
            dayOfMonth: update_field.dayOfMonth,
            monthOfYear: update_field.MonthOfYear,
          },
        });
      } else {
        onChangeProp({ ...value, startDate: selectedTime, ...update_field });
      }
    },
    [onChangeProp, value],
  );

  const { isMobile } = useScreen();
  const { Component } = FrequencyComponents[value.frequency];

  return (
    <Box className="flex flex-col">
      <LabelAndDescription
        label={t('1on1s:Modal.Frequency')}
        mergedOverrides={headerOverrides ?? getDefaultOverrides(isMobile)}
      />
      <Component
        allowedCustomFrequencies={allowedCustomFrequencies}
        allowedFrequencies={allowedFrequencies}
        disabled={disabled}
        errorConfig={errorConfig}
        hideTimeSelection={hideTimeSelection}
        overrides={overrides}
        size={size}
        value={value}
        onChange={onChange}
      />
      {moduleName ? (
        <StartsOnDateField
          moduleName={moduleName}
          value={value.startDate!}
          onChange={onChangeDateField}
        />
      ) : null}
      <Warnings warnings={warnings} />
    </Box>
  );
}

export default React.memo(FrequencyFormField);
